import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy, Medal, MapPin, Crown, Landmark, Sparkles, ArrowLeft } from "lucide-react";
import UserBadges from "@/components/UserBadges";
import MysteryInfo from "@/components/MysteryInfo";

const FOUNDER_TIER = ["Found the Founder", "Loot Disciple", "Local Legend"];

function BadgePill({ label }) {
  if (FOUNDER_TIER.includes(label)) {
    return (
      <span
        className="flex items-center gap-1 px-3 py-1 font-display uppercase text-xs tracking-widest"
        style={{
          background: "linear-gradient(135deg, #D7A62A, #F5D066, #D7A62A)",
          color: "#0F0F0F",
          border: "2px solid #D7A62A",
          boxShadow: "0 0 12px rgba(215,166,42,0.35)",
        }}
        data-testid={`badge-${label.replace(/\s+/g, "-").toLowerCase()}`}
      >
        <Crown size={12} /> {label}
        <span className="ml-1 font-mono text-[8px] opacity-70">RARE</span>
      </span>
    );
  }
  return (
    <span
      className="tag-pill flex items-center gap-1 px-3 py-1"
      style={{ color: "#76B82A", borderColor: "#76B82A" }}
      data-testid={`badge-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <Medal size={12} /> {label}
    </span>
  );
}

export default function HunterProfilePage() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/users/${encodeURIComponent(username)}/public`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Hunter not found");
      }
    })();
    return () => { alive = false; };
  }, [username]);

  if (error) {
    return (
      <div className="py-20 text-center" data-testid="hunter-not-found">
        <div className="overline mb-2">Hunter</div>
        <h1 className="font-display uppercase text-4xl">Not found</h1>
        <p className="mt-3 text-[#F5F2E8]/60">{error}</p>
        <Link to="/winners" className="btn-ghost mt-6 inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Back to Winners
        </Link>
      </div>
    );
  }
  if (!data) return <div className="py-20 text-[#F5F2E8]/60 text-center">Loading…</div>;

  const u = data.user;
  const s = data.stats;

  return (
    <div className="py-8 space-y-8" data-testid="hunter-profile-page">
      {/* HERO */}
      <div className="loot-card-flat p-8 flex flex-col md:flex-row gap-6 items-start">
        <div
          className="w-24 h-24 flex items-center justify-center font-display text-5xl shrink-0"
          style={{ background: "#123A1A", color: "#76B82A" }}
        >
          {(u.username || u.name || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline">Hunter profile</div>
          <h1
            className="font-display uppercase text-4xl md:text-5xl tracking-tight mt-2 break-all inline-flex items-center gap-2 flex-wrap"
            data-testid="hunter-username"
          >
            @{u.username}
            <UserBadges
              lootLegend={u.loot_legend}
              goodDeeds={u.good_deeds_count}
              igniter={u.igniter_status}
              badges={u.badges}
              size={14}
              testIdPrefix="hunter-profile-badge"
            />
          </h1>
          {u.name && <div className="mt-1 text-sm text-[#F5F2E8]/60">{u.name}</div>}
          {u.city && (
            <div className="mt-2 font-mono text-xs text-[#F5F2E8]/55 flex items-center gap-1">
              <MapPin size={12} /> {u.city}
            </div>
          )}
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-3 gap-3">
        <div className="loot-card-flat p-4 text-center" data-testid="stat-wins">
          <div className="flex items-center justify-center text-[#76B82A] gap-1">
            <Trophy size={18} /> <span className="font-display text-3xl">{s.wins}</span>
          </div>
          <div className="font-mono text-[10px] text-[#F5F2E8]/60 uppercase tracking-widest mt-1">
            Wins
          </div>
        </div>
        <div className="loot-card-flat p-4 text-center" data-testid="stat-waymarks">
          <div className="flex items-center justify-center text-[#D7A62A] gap-1">
            <Landmark size={18} /> <span className="font-display text-3xl">{s.waymarks_unlocked}</span>
          </div>
          <div className="font-mono text-[10px] text-[#F5F2E8]/60 uppercase tracking-widest mt-1">
            Waymarks
          </div>
        </div>
        <div className="loot-card-flat p-4 text-center" data-testid="stat-founder">
          <div className="flex items-center justify-center text-[#E53935] gap-1">
            <Sparkles size={18} /> <span className="font-display text-3xl">{s.founder_meets}</span>
          </div>
          <div className="font-mono text-[10px] text-[#F5F2E8]/60 uppercase tracking-widest mt-1">
            Founder meets
          </div>
        </div>
      </div>

      {/* BADGES */}
      <div>
        <div className="overline mb-3">Badges collected</div>
        <div className="flex flex-wrap gap-2" data-testid="badges-list">
          {u.badges.length === 0 ? (
            <div className="text-[#F5F2E8]/55 text-sm">
              No badges yet — every hunter starts here.
            </div>
          ) : (
            u.badges.map((b) => <BadgePill key={b} label={b} />)
          )}
        </div>
      </div>

      {/* CITIES */}
      {(s.win_cities.length > 0 || s.founder_cities.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {s.win_cities.length > 0 && (
            <div className="loot-card-flat p-5">
              <div className="overline mb-2">Cities won in</div>
              <div className="flex flex-wrap gap-2">
                {s.win_cities.map((c) => (
                  <Link
                    key={c}
                    to={`/hunts/${encodeURIComponent(c)}`}
                    className="tag-pill px-3 py-1 hover:opacity-80"
                    style={{ color: "#76B82A", borderColor: "#76B82A" }}
                  >
                    <MapPin size={10} className="inline mr-1" />
                    {c}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {s.founder_cities.length > 0 && (
            <div className="loot-card-flat p-5" style={{ borderColor: "rgba(215,166,42,0.4)" }}>
              <div className="overline mb-2" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>
                Met the founder in
              </div>
              <div className="flex flex-wrap gap-2">
                {s.founder_cities.map((c) => (
                  <span
                    key={c}
                    className="tag-pill px-3 py-1"
                    style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                  >
                    <Crown size={10} className="inline mr-1" />
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* RECENT WINS */}
      {data.recent_wins.length > 0 && (
        <div>
          <div className="overline mb-3">Recent finds</div>
          <div className="space-y-2">
            {data.recent_wins.map((w) => {
              const isCash = (w.prize_type || "cash") === "cash";
              let headline;
              if (w.is_mystery) headline = "🎁 Found Mystery Loot";
              else if (isCash) headline = w.hide_amount ? "Found CASH" : `Found $${w.amount}`;
              else headline = `Found ${(w.prize_type || "").toUpperCase()}${w.prize_custom_label ? ` · ${w.prize_custom_label}` : ""}`;
              return (
                <div
                  key={w.id}
                  className="loot-card-flat p-4 flex items-center justify-between gap-3"
                  data-testid={`recent-win-${w.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-display uppercase text-lg leading-tight truncate">
                      {w.city} · {headline}
                      {w.is_mystery && <MysteryInfo size={12} />}
                    </div>
                    <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">
                      {w.created_at ? new Date(w.created_at).toLocaleDateString() : ""}
                    </div>
                  </div>
                  <Link to="/winners" className="btn-ghost text-xs shrink-0">
                    View
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* FOOTER LINK */}
      <div>
        <Link to="/winners" className="btn-ghost inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Back to Winners Wall
        </Link>
      </div>
    </div>
  );
}
